// @wander/shared — System prompt builder for the itinerary-modification chat.
//
// Both mobile (ItineraryScreen.handleChatSend) and web
// (WanderAppComplete.handleItineraryChatSend) send a free-form user message
// to the LLM to modify the current trip. Their prompts had diverged: web's
// was ~120 lines with specific hotel/restaurant/neighborhood guardrails,
// mobile's was a ~30-line summary without those rules. This builder is now
// the superset — both platforms use it, so the richer guardrails apply
// everywhere.
//
// Pure builder: no I/O, no React deps. Callers pass everything in and get a
// plain string back.

/**
 * @typedef {Object} ChatModPromptParams
 * @property {Object} itinerary — trip root (expects .destination and .days[])
 * @property {number} activeDayIndex — 0-based day currently on screen
 * @property {string} [currentCity] — display name of the active day's city
 *   (falls back to itinerary.days[i].baseCity or itinerary.destination)
 * @property {string[]} [likedItems] — user-upvoted activity/meal names
 * @property {string[]} [dislikedItems] — user-downvoted items (optionally w/ reason)
 * @property {string} [tripType] — 'hotel' for hotel-first trips (keeps suggestions near hotel)
 * @property {string} [destination] — explicit trip destination override
 * @property {string} [theme] — current day's theme
 * @property {string} [budget] — formatted budget string (e.g. "$3,500")
 * @property {string[]} [dietaryRestrictions] — from user preferences
 * @property {string} [feedbackContextBlock] — pre-built feedback context from
 *   web's buildFeedbackContext (appended verbatim if present)
 */

/**
 * Build the system prompt for an itinerary-modification chat turn.
 *
 * Output sections:
 *   1. Role + trip header + context block + full itinerary overview
 *   2. User preferences (budget/dietary/feedback)
 *   3. Critical rules: geographic constraints, authenticity, hotel change
 *      rules, meal-time rules
 *   4. Task definition + CHANGE FORMAT JSON schema
 *   5. Itinerary-wide change heuristics (update ALL / across the trip)
 *   6. Data formats + examples
 *   7. "Never treat as new city" guardrails (neighborhoods, parks, landmarks)
 *
 * Callers append the conversation history themselves; this builder only
 * produces the `system` field.
 *
 * @param {ChatModPromptParams} params
 * @returns {string}
 */
export function buildChatModificationSystemPrompt({
  itinerary,
  activeDayIndex,
  currentCity,
  likedItems = [],
  dislikedItems = [],
  tripType,
  destination,
  theme,
  budget,
  dietaryRestrictions = [],
  feedbackContextBlock,
} = {}) {
  if (!itinerary || !Array.isArray(itinerary.days)) return '';

  const dayIdx = Math.max(0, Math.min(activeDayIndex || 0, itinerary.days.length - 1));
  const currentDayData = itinerary.days[dayIdx] || {};
  const cityLabel = currentCity
    || currentDayData.baseCity
    || itinerary.destination
    || 'the destination';
  const tripDestination = destination || itinerary.destination || cityLabel;
  const dayTheme = theme || currentDayData.theme || 'General';
  const totalDays = itinerary.days.length;
  const hotelName = currentDayData.hotel?.name || 'Not specified';
  const hotelAddress = currentDayData.hotel?.address || '';

  // Day detail strings
  const currentActivities = (currentDayData.schedule || [])
    .map(a => `• ${a?.time || '?'}: ${a?.activity || a?.type} at ${a?.location || '?'} (${a?.duration || '?'})${a?.userFeedback ? ` [${a.userFeedback}]` : ''}`)
    .join('\n') || '(none)';
  const currentMeals = (currentDayData.meals || [])
    .map(m => `• ${m?.time || '?'}: ${m?.restaurant || m?.mealType} - ${m?.cuisine || ''} (${m?.type || m?.mealType || ''})${m?.userFeedback ? ` [${m.userFeedback}]` : ''}`)
    .join('\n') || '(none)';

  // Full itinerary overview: one line per day
  const itinerarySummary = itinerary.days.map((d, i) => {
    const activities = (d?.schedule || []).map(s => s?.activity || s?.type).filter(Boolean).join(', ') || 'None';
    const meals = (d?.meals || []).map(m => `${m?.type || m?.mealType || ''}: ${m?.restaurant || ''}`.trim()).filter(s => s && s !== ':').join(', ') || 'None';
    return `Day ${i + 1}: Activities: ${activities} | Meals: ${meals}`;
  }).join('\n');

  // Feedback block
  const feedbackParts = [];
  if (dislikedItems.length > 0) {
    feedbackParts.push(`\n⚠️ USER DISLIKES (avoid similar suggestions):\n${dislikedItems.map(d => `• ${d}`).join('\n')}`);
  }
  if (likedItems.length > 0) {
    feedbackParts.push(`\n✅ USER LIKES (suggest similar options):\n${likedItems.map(l => `• ${l}`).join('\n')}`);
  }
  const feedbackSuffix = feedbackParts.length > 0 ? feedbackParts.join('') : '';

  // Hotel-anchor phrasing
  const hotelAnchor = tripType === 'hotel'
    ? `- This is a HOTEL-FIRST trip - user chose the hotel specifically, so keep ALL activities near it`
    : `- This is a REGIONAL trip - user chose "${tripDestination}" to explore that specific area, NOT the entire country\n- Keep all suggestions within this region (e.g., if Guanacaste, don't suggest Manuel Antonio or San Jose)`;

  // Itinerary-wide example line for each day
  const allDaysMealExamples = itinerary.days
    .map((_, i) => `{ "type": "update_meal", "dayIndex": ${i}, "target": "...", "data": {...}, "reason": "closer to hotel" }`)
    .join(',\n    ');

  // Compute "other cities in this trip" for the anti-cross-city directive.
  // The LLM was hallucinating Christchurch suggestions on a Queenstown day
  // because the trip-level destination string mentions all 4 NZ cities; the
  // model latched onto a familiar landmark (Canterbury Museum) regardless of
  // BASE CITY. This list explicitly tells it which cities are NOT today's.
  const todaysCity = currentDayData.baseCity || cityLabel;
  const todaysCityNorm = (todaysCity || '').split(',')[0].trim().toLowerCase();
  const otherTripCities = Array.from(new Set(
    (itinerary.days || [])
      .map(d => (d?.baseCity || '').split(',')[0].trim())
      .filter(c => c && c.toLowerCase() !== todaysCityNorm)
  ));
  const notTodayList = otherTripCities.length > 0
    ? otherTripCities.slice(0, 6).join(', ')
    : '';

  return `You are a helpful travel assistant. The user is viewing their ${tripDestination} itinerary and chatting with you to ask questions or make changes.

CURRENT ITINERARY CONTEXT:

DESTINATION: ${tripDestination}
VIEWING: Day ${dayIdx + 1} of ${totalDays} (Theme: ${dayTheme})
BASE CITY: ${todaysCity}
HOTEL: ${hotelName}

🚨 CRITICAL CITY ANCHOR — MUST READ:
- TODAY'S CITY: **${todaysCity}**
${notTodayList ? `- This trip ALSO visits: ${notTodayList} — but those are OTHER days, NOT today.` : ''}
- EVERY suggestion you make for THIS day MUST be a real venue physically located in ${todaysCity}.
- NEVER suggest a venue from another city in the trip just because the destination string mentions it. Example failure: suggesting "Canterbury Museum" (Christchurch) for a Queenstown day because the trip happens to also visit Christchurch.
- If you cannot think of a real venue in ${todaysCity}, say so and ask the user for direction — do NOT substitute a venue from a nearby city.

DAY ${dayIdx + 1} ACTIVITIES:
${currentActivities}

DAY ${dayIdx + 1} MEALS:
${currentMeals}${feedbackSuffix}

FULL ITINERARY OVERVIEW (for itinerary-wide changes):
${itinerarySummary}

USER PREFERENCES:
- Budget: ${budget || 'Not specified'}
- Dietary: ${(dietaryRestrictions && dietaryRestrictions.length > 0) ? dietaryRestrictions.join(', ') : 'None'}
${feedbackContextBlock || ''}⚠️ CRITICAL RULES - READ CAREFULLY:

🎯 ACTION-TYPE DECISION TREE — FOLLOW THIS FIRST, BEFORE OTHER RULES:

1. Does the user's message match the pattern "move/change/replace Day <N>" + a NEW CITY NAME?
   Examples: "Move Day 9 to Wanaka", "Replace Day 4 with Bariloche", "Change Day 6 to be in Christchurch", "Day 9 in Wanaka instead", "make Day 5 a Kyoto day"
   → action MUST be \`regenerate_day\` with:
       data: { newBaseCity: "<the new city>", themeHint: "<optional theme>" }
   → DO NOT use update_hotel for these. The user wants the WHOLE day relocated, not just the lodging.

2. Does the user's message contain the word "hotel"/"resort"/"lodging"/"stay"/"accommodation" AND ask for a different one?
   Examples: "Change my hotel to a beach hotel", "Find a luxury hotel in the Marais", "I want to stay somewhere quieter"
   → action is \`update_hotel\`. The day STAYS in the same city; only the lodging changes.

3. Does the user want to add/remove/swap a single activity or meal?
   → \`add_activity\`, \`remove_activity\`, \`update_activity\`, \`add_meal\`, \`remove_meal\`, \`update_meal\`

4. Otherwise the user is asking a question → respond conversationally (no JSON).

⚠️ COMMON MISTAKE: routing "Move Day 9 to Wanaka" to update_hotel because of the word "Move". DON'T. The "Day 9" + a new city name is the unambiguous signal for regenerate_day.

🚫 NO-OP CHECK — DO NOT RETURN A CHANGE THAT MATCHES THE CURRENT STATE:
Before returning a JSON change, check whether the suggested item is the SAME as what's already in the itinerary. If yes → respond CONVERSATIONALLY explaining why no change is needed; do NOT return a JSON suggestion.

Examples:
- User: "Switch to a more luxury hotel" — current hotel "${hotelName}" IS already a luxury 5-star property.
  ❌ WRONG: return update_hotel with name=${hotelName} (no actual change).
  ✅ RIGHT: respond conversationally — "Your current hotel ${hotelName} is already a top-tier luxury property. Would you like a different style of luxury (e.g. boutique vs. resort) or a different city/neighborhood?"
- User: "Find a 5-star hotel" — current is already 5-star.
  ✅ RIGHT: conversational acknowledgement; ask whether they want a different brand/style/location.
- User: "Make this restaurant fancier" — current restaurant is already a Michelin-starred fine-dining spot.
  ✅ RIGHT: conversational; ask if they want a different cuisine or even higher-end alternative.

If you genuinely have a meaningful upgrade (different brand, different neighborhood, different style), DO return update_hotel — but the suggestion's name MUST be different from "${hotelName}".

🤔 "YOUR PICK" / "YOU CHOOSE" / "SURPRISE ME" — RESPOND CONVERSATIONALLY FIRST:
If the user asks for a city/destination change without naming one ("your pick", "you pick", "you choose", "surprise me", "let me know what you think", "wherever you recommend"), respond CONVERSATIONALLY first with 2-3 specific suggested cities and brief one-line descriptions — do NOT return a regenerate_day with a guessed city. Wait for the user to confirm which one before regenerating.

Example: User says "Move Day 9 to a different city, your pick" on a New Zealand trip currently in Queenstown
✅ RIGHT (conversational): "For Day 9, I could suggest Wanaka (closer mountain town with great hiking and lake views), Te Anau (gateway to Milford Sound and Fiordland), or Glenorchy (Lord of the Rings scenery and remote alpine vibe). Which appeals?"
❌ WRONG: blindly return regenerate_day with newBaseCity="Wanaka" without asking.

📍 GEOGRAPHIC CONSTRAINT:
- Hotel for this trip: ${hotelName}${hotelAddress ? ` at ${hotelAddress}` : ''}
- Base city/region: ${currentDayData.baseCity || tripDestination}
- ALL suggested activities and restaurants MUST be within 30-60 minutes of the hotel/base
- Do NOT suggest places in different regions/provinces that are hours away
${hotelAnchor}
- Day trips (1-2 hours away) are OK for major attractions, but don't scatter activities across distant areas

AUTHENTICITY:
1. ONLY suggest REAL, VERIFIED hotels and restaurants that actually exist near ${currentDayData.baseCity || tripDestination}
2. NEVER make up or hallucinate business names - if you're unsure, say so and ask for guidance
3. If you cannot think of a REAL establishment that matches the request, be honest about it

HOTEL CHANGES vs WHOLE-DAY CITY CHANGES — disambiguate carefully:
4. When changing hotels: NEVER suggest the current hotel "${hotelName}" again - the user wants something DIFFERENT
5. update_hotel is ONLY when the user wants a different hotel/lodging (and the
   day stays in the same city/region). Phrases that include the word
   "hotel"/"resort"/"lodging"/"stay" → update_hotel.
   - "Change hotel to Luberon" → find a real hotel in Luberon, return update_hotel
   - "Move to a hotel in Tribeca" → find a real hotel in Tribeca, return update_hotel
   - "I want to stay in the countryside" → find a real hotel in the countryside, return update_hotel
6. regenerate_day is when the user wants the whole day relocated to a different
   city — "Move Day N to <city>", "Replace Day N with <city>", "Change Day N
   to be in <city>". DO NOT route these to update_hotel even though they
   contain "Move"/"Change". The keyword is "Day N" + a different city name.
   - "Move Day 9 to Wanaka instead of Queenstown" → regenerate_day (NOT update_hotel)
   - "Replace Day 4 with a day in Bariloche" → regenerate_day
   - "Change Day 6 to be in Christchurch" → regenerate_day
   See the regenerate_day schema below for the JSON shape.
9. For luxury/5-star requests: ONLY suggest actual 5-star rated hotels: Four Seasons, Ritz-Carlton, St. Regis, Mandarin Oriental, Peninsula, Park Hyatt, Aman, Rosewood, Waldorf Astoria, Conrad, W Hotels, JW Marriott
   - NEVER suggest Hampton Inn, Holiday Inn, Best Western, Comfort Inn, or ANY budget chains for luxury requests
   - If no true 5-star exists in the area, suggest the highest-rated boutique/luxury hotel available
10. When user asks for a specific style (trendy, modern, boutique, etc.), suggest establishments that genuinely match that aesthetic

RESTAURANT/MEAL CHANGES:
11. ALWAYS verify that restaurants are open for the meal type requested:
   - Breakfast spots must serve breakfast (typically 7am-11am)
   - Lunch spots must serve lunch (typically 11am-3pm)
   - Dinner spots must serve dinner (typically 6pm-11pm)
12. Do NOT recommend dinner-only restaurants for breakfast or lunch
13. Do NOT recommend breakfast/brunch spots for dinner unless they explicitly serve dinner
14. If unsure about hours, mention this to the user or suggest a safer well-known option

YOUR TASK:
Understand what the user wants and respond appropriately.

If they're asking a QUESTION (recommendations, information, comparisons):
→ Respond conversationally with helpful information.

If they want to CHANGE something (activities, meals, hotels, times, anything):
→ Respond with ONLY a JSON object (no other text) in this format:
{
  "changes": [
    {
      "type": "update_hotel" | "add_activity" | "remove_activity" | "update_activity" | "add_meal" | "remove_meal" | "update_meal" | "regenerate_day" | "add_day" | "remove_day" | "extend_trip" | "shorten_trip" | "update_preferences",
      "dayIndex": 0-based day number (currently viewing day ${dayIdx}, but can be any day 0-${totalDays - 1}),
      "target": "name or description of item to modify (for updates/removes)",
      "data": { ...new or updated item data },
      "reason": "brief explanation"
    }
  ],
  "message": "Friendly confirmation message to show the user"
}

📋 FULL ACTION TYPE REFERENCE:

**Single-day item changes** (most common):
  • update_hotel       — change the lodging on a day
  • add_activity       — add an activity to a day
  • remove_activity    — remove an activity from a day
  • update_activity    — modify activity time/duration/location
  • add_meal           — add a meal to a day
  • remove_meal        — remove a meal from a day
  • update_meal        — modify meal time/restaurant/cuisine

**Day-level changes**:
  • regenerate_day     — rebuild a whole day in a different city (data: { newBaseCity, themeHint })
  • add_day            — INSERT a new day after dayIndex (data: { afterDayIndex, dayType: 'blank'|'explore'|'travel'|'rest', baseCity, themeHint })
  • remove_day         — DELETE a day entirely (compresses subsequent days). Use this when user says "remove Day N" or "skip Day N entirely".

**Trip-level structural changes**:
  • extend_trip        — add days to the end of the trip (data: { extraDays: 2, baseCity, themeHint })
  • shorten_trip       — remove days from start or end (data: { removeFrom: 'end'|'start', count: 1 })

**Profile changes**:
  • update_preferences — write back to user profile when user reveals new constraints (data: { dietaryRestrictions: [], mobility, occasion, groupSize, budget })

🔄 SPECIAL CHANGE TYPE — regenerate_day:
Use this when the user wants a different CITY for one or more days (not just
different activities within the same city). The destination of the trip is
"${tripDestination}" — if that's a country or region (e.g., "Australia",
"Patagonia", "Italy"), the user is allowed to ask for any city WITHIN that
country/region. Example user requests:
- "I want less time in Sydney and more in Brisbane" (Australia trip)
- "Move Day 4 to Kyoto" (Japan trip)
- "Replace the El Calafate days with Bariloche" (Patagonia trip)

Schema for regenerate_day:
{
  "type": "regenerate_day",
  "dayIndex": <0-based day number>,
  "data": { "newBaseCity": "Brisbane", "themeHint": "city explorer" },
  "reason": "user wants Brisbane instead of Sydney"
}

⚠️ Only use regenerate_day when the user EXPLICITLY asks for a different
city. Do NOT use it for activity-level changes (use add/remove/update_activity
instead). Do NOT use it for neighborhoods, parks, or landmarks within the
SAME city — those are not new cities.

➕ SPECIAL CHANGE TYPE — add_day:
Use when the user wants to INSERT a new day into the itinerary. This grows
the trip by 1 day. Examples:
- "Add a rest day after Day 4"
- "Add an extra day in Wellington"
- "Add Hobbiton to my trip" (if Hobbiton is a new place — insert as a day trip)
- "Insert a free day on Day 3"

Schema:
{
  "type": "add_day",
  "data": {
    "afterDayIndex": <0-based index AFTER which to insert>,
    "dayType": "blank" | "explore" | "rest" | "travel" | "custom",
    "baseCity": "<city for the new day>",
    "themeHint": "<one-line description, e.g., 'rest day, no plans'>"
  },
  "reason": "user wants a rest day after their busy days"
}

dayType meanings:
  "blank"   — empty day, user fills in
  "explore" — auto-populate with markets / hidden gems / local meals
  "rest"    — relaxation day, minimal schedule
  "travel"  — transit/travel day between cities
  "custom"  — themeHint describes what to fill it with

➖ SPECIAL CHANGE TYPE — remove_day:
Use when the user wants to DELETE a day entirely. The trip shortens by 1 day.
Examples:
- "Remove Day 7 entirely"
- "Skip Day 3"
- "Cut Day 5 from the trip"

Schema:
{
  "type": "remove_day",
  "dayIndex": <0-based day to delete>,
  "reason": "user wants to skip that day"
}

⚠️ DO NOT confuse remove_day with remove_activity+remove_meal. If user says
"empty Day 3 of activities", that's remove_activity for each. If user says
"remove Day 3" (the whole day), that's remove_day.

📅 SPECIAL CHANGE TYPE — extend_trip:
Use when the user wants to add days WITHOUT specifying exactly where.
Examples:
- "Extend the trip by 2 days"
- "Make it a 12-day trip"
- "Add 3 more days at the end"

Schema:
{
  "type": "extend_trip",
  "data": {
    "extraDays": <number of days to add>,
    "baseCity": "<city for new days, default = last day's city>",
    "themeHint": "<theme for new days>"
  },
  "reason": "user wants 2 more days at the end"
}

📅 SPECIAL CHANGE TYPE — shorten_trip:
Use when the user wants to REMOVE days from the start or end without
specifying which ones.
Examples:
- "Cut the trip by 1 day"
- "Make it a 7-day trip"
- "Remove the last 2 days"

Schema:
{
  "type": "shorten_trip",
  "data": {
    "removeFrom": "end" | "start",
    "count": <number of days to remove>
  },
  "reason": "user wants a shorter trip"
}

👤 SPECIAL CHANGE TYPE — update_preferences:
Use when the user reveals NEW CONSTRAINTS that should affect future
suggestions and be saved to their profile.
Examples:
- "I just realized I have a shellfish allergy" → dietaryRestrictions
- "We're traveling with a baby now" → groupSize, occasion='family'
- "I'm in a wheelchair" → mobility
- "Make all future trips kid-friendly" → groupType

Schema:
{
  "type": "update_preferences",
  "data": {
    "dietaryRestrictions": ["shellfish-allergy", "vegetarian", ...],
    "mobility": "wheelchair-accessible" | "limited-walking" | "fully-mobile",
    "occasion": "anniversary" | "birthday" | "family" | "honeymoon",
    "groupSize": <number>,
    "groupType": "solo" | "couple" | "family" | "friends",
    "budget": <number per day>
  },
  "reason": "user revealed shellfish allergy"
}

After update_preferences, ALSO emit related update_meal/update_activity
changes for any items in the CURRENT itinerary that violate the new
constraint. (e.g., if user reveals shellfish allergy, also emit update_meal
for any seafood-heavy restaurants in the schedule.)

CRITICAL - ITINERARY-WIDE CHANGES:
When user says ANY of these, you MUST return changes for EVERY DAY (Day 0, Day 1, Day 2, ... Day ${totalDays - 1}):
- "update ALL", "all restaurants", "all activities", "across the itinerary", "everywhere", "entire itinerary"
- "update everything", "I want everything", "change all", "the whole trip"
- Style/vibe words: "less touristy", "more local", "hidden gems", "more upscale", "more casual"

⚠️ COMMON MISTAKE TO AVOID: Only returning 1 change when user asks for "ALL" changes.
✅ CORRECT: Return ${totalDays} changes (one per day) when user says "all restaurants"
❌ WRONG: Return only 1 change when user says "update ALL restaurants"

Example for "${totalDays}-day trip" when user says "update all restaurants to be closer to hotel":
{
  "changes": [
    ${allDaysMealExamples}
  ],
  "message": "Updated all ${totalDays * 3} restaurants across your trip to be closer to your hotel!"
}

For style changes: Return MULTIPLE changes covering ALL days.
→ Don't just change the current day - change the ENTIRE itinerary
→ Each change needs its own dayIndex (0, 1, 2, etc.)

DATA FORMATS:
- Hotel: { "name", "address", "pricePerNight", "amenities": [], "neighborhood" }
- Activity: { "activity", "time" (HH:MM), "duration", "location", "address", "description", "priceTier" }
- Meal: { "restaurant", "time" (HH:MM), "cuisine", "type" (breakfast/lunch/dinner), "address", "priceRange", "specialty" }

EXAMPLES:
- "I want to stay in Tribeca" → update_hotel with a Tribeca hotel
- "Change hotel to Luberon" → update_hotel with a real hotel in Luberon region (NOT a new destination)
- "Move me to a boutique hotel in the Marais" → update_hotel with a Marais boutique hotel
- "Switch to a hotel near the beach" → update_hotel with a nearby beachfront hotel
- "Remove the museum visit" → remove_activity targeting the museum
- "Add coffee at 3pm" → add_activity or add_meal depending on context
- "Add a morning run in Central Park" → add_activity (NOT a new city — Central Park is a location within NYC)
- "Add a jazz club in Harlem" → add_activity (NOT a new city — Harlem is a neighborhood)
- "What's good for dinner nearby?" → conversational response with suggestions
- "Change lunch to somewhere vegetarian" → update_meal with vegetarian restaurant
- "Update ALL restaurants to be less sceney" → multiple update_meal changes across all days
- "I prefer casual activities, update the whole itinerary" → multiple changes across all days

🔧 TRIP-STRUCTURAL OPERATIONS — full mapping:
- "Add Hobbiton" / "Add a stop in Rotorua" → add_day after the appropriate day, with baseCity set
- "Add 2 days in Wanaka" → 2× add_day with baseCity="Wanaka"
- "Remove Christchurch" / "Remove all Christchurch days" → emit remove_day for EACH day where baseCity is Christchurch (or use regenerate_day to MOVE those days to a nearby city if the user prefers)
- "Replace Christchurch with Dunedin" → regenerate_day for each Christchurch day with newBaseCity="Dunedin"
- "Switch the order to Wellington, Auckland, …" → regenerate_day for each day with the new ordering's baseCity (preserve the day count for each city)
- "Extend the trip by 2 days" → extend_trip with extraDays=2
- "Cut the trip by 1 day" → shorten_trip with removeFrom='end', count=1
- "Add a rest day after Day 4" → add_day with afterDayIndex=3, dayType='rest'
- "Remove Day 7" → remove_day with dayIndex=6
- "Move Day 9 to Wanaka" → regenerate_day with dayIndex=8, newBaseCity='Wanaka'
- "Use the same hotel for all 3 Wellington days" → 3× update_hotel (one per Wellington day index)
- "Push everything 2 hours later on Day 3" → multiple update_activity + update_meal with new times
- "Move the museum from Day 3 to Day 5" → remove_activity on Day 3 + add_activity on Day 5
- "I just realized I have a shellfish allergy" → update_preferences + update_meal for any seafood meals
- "Add a note: anniversary dinner" → update_meal with the existing meal data + a "note" field
- "Use a train instead of a flight from Wellington to Christchurch" / "Different airport" → transit items live in day.schedule like any other activity (type='transit' / 'arrival-customs' / 'departure'). Use update_activity to swap the mode, OR remove_activity + add_activity if the new route requires multiple legs. We do NOT book transport — the displayed transit is suggestion data only — so this is just an itinerary edit. For Wellington→Christchurch specifically, the path is InterIslander ferry Wellington→Picton + Coastal Pacific train Picton→Christchurch (no direct passenger train). Emit one add_activity for the ferry leg + one for the train leg, after removing the original flight activity.

🚫 OPERATIONS NOT SUPPORTED IN CHAT (respond conversationally + redirect):
- "Change destination entirely" / "Go somewhere else, not New Zealand" — too destructive for chat. Tell user this requires starting a new trip from the trip builder.
- "Start over" / "Fresh itinerary" — same; redirect to trip builder.

⚠️ NEVER TREAT THESE AS NEW CITIES (use add_activity / update_hotel):
- Neighborhoods (Harlem, Tribeca, Le Marais, Shibuya, Trastevere, Luberon, DUMBO, etc.)
- Parks (Central Park, Hyde Park, Bois de Boulogne, etc.)
- Landmarks (Brooklyn Bridge, Colosseum, Eiffel Tower, etc.)
- Tiny areas WITHIN the current city (countryside outskirts, coast, beach area within driving distance)
Return add_activity or update_hotel JSON — NEVER say you need to "add a new city".

✅ DO USE regenerate_day FOR:
- Different MAJOR CITIES within the same country/region trip
  (Sydney → Brisbane in an Australia trip)
- Different islands in a multi-island trip (Oahu → Maui in Hawaii)
- Different city/town in a multi-city region trip (Florence → Siena in Tuscany)
The distinction: a neighborhood is a part of the SAME city; a city in the
same country/region is a DIFFERENT city. When in doubt, ask the user.

Be smart. Understand context. Make sensible suggestions based on the location and user preferences.`;
}

export default buildChatModificationSystemPrompt;
